/**
 * Interview flow config — drives the candidate experience (steps, verification, recording,
 * requirements, theme).
 *
 * Source of truth is the BACKEND (`ai_interview_configs`, resolved per company → session).
 * localStorage is kept only as a fast cache / offline fallback so the UI never blocks on the
 * network. Use `fetchInterviewConfig` (async, authoritative) to load and `saveInterviewConfigRemote`
 * to persist; the sync `getInterviewConfig` returns the cached value (or defaults).
 */
import {
  getInterviewConfig as apiGetConfig,
  saveConfigDefaults as apiSaveDefaults,
} from '../../ai-interview/utils/aiInterviewApi';

const KEY = 'wizbox_interview_config';

export const DEFAULT_CONFIG = {
  steps: { permission: true, verification: true, instruction: true },
  verify: { photo: true, voice: true, network: true },
  // screen = entire screen + system audio + mic audio in one file. camera/audio = optional SEPARATE
  // files, off by default (the audio is already inside the screen recording).
  record: { screen: true, camera: false, audio: false },
  interview: {
    camera_required: true,
    camera_strict: false,   // must stay on for the whole interview
    screen_required: true,
    screen_strict: false,
    mic_required: true,
  },
  theme_mode: 'light',      // dark | light | auto
};

function isObj(v) { return v && typeof v === 'object' && !Array.isArray(v); }

function deepMerge(base, over) {
  const out = { ...base };
  Object.keys(over || {}).forEach((k) => {
    out[k] = isObj(base[k]) && isObj(over[k]) ? deepMerge(base[k], over[k]) : over[k];
  });
  return out;
}

/** Effective config: defaults deep-merged with whatever the admin saved in localStorage. */
export function getInterviewConfig() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? deepMerge(DEFAULT_CONFIG, JSON.parse(raw)) : { ...DEFAULT_CONFIG };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

/** Persist the full config object (already merged) as JSON. */
export function saveInterviewConfig(cfg) {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch { /* ignore quota/unavailable */ }
  return cfg;
}

export function resetInterviewConfig() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  return { ...DEFAULT_CONFIG };
}

/**
 * Authoritative load: resolve the effective config from the backend (DEFAULTS → company → session)
 * and refresh the local cache. Falls back to the cached value / defaults if the backend is down.
 */
export async function fetchInterviewConfig(companyId, sessionId, candidateId) {
  try {
    const remote = await apiGetConfig(companyId, sessionId, candidateId);
    const merged = deepMerge(DEFAULT_CONFIG, remote || {});
    try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch { /* ignore */ }
    return merged;
  } catch {
    return getInterviewConfig();
  }
}

/**
 * Admin save: persist to the backend (source of truth, reaches every device) then update the
 * local cache. Throws if the backend write fails so the admin sees a real error.
 */
export async function saveInterviewConfigRemote(cfg, companyId, sessionId = '') {
  await apiSaveDefaults(cfg, companyId, sessionId);
  saveInterviewConfig(cfg);
  return cfg;
}
